import { isLocaleFormat } from '@edicom/common/number/isLocaleFormat';

export default {
  setCommonAttributes,
};

/**
 * Devuelve la definición base de la columna para ec-grid-edit transformando el viewField.
 * Realmente se podría pasar el viewField tal cual,
 * que de hecho cloneamos (sin `parent`, ni `properties`)
 * menos para ciertas propiedades que se tienen que transformar:
 *
 * - model: el grid no soporta los padres, el model debe ser a partir del padre. ej para array "CAB.LIN" el campo "CAB.LIN.EAN" debe ser model: "EAN"
 * - xPath: como cambiamos el `model`, añadimos el xPath (que es el `model` completo)
 * - default: el grid soporta expressiones, entonces viewField.default -> 'viewField.default' y/o/ viewField.metadata.defaultEval -> tal cual
 * - required: el grid acepta {boolean} o {funcion} entonces expresiones se transforman a funciones.
 * - disabled: idem required
 * - invalid: idem required
 * - mask: para tipos `number`, se transforma a localeFormat
 * - pattern: se transforma de forma estandar
 * - pin: se añade la opción si el viewField.name esta definido en el metadata.pin|pinEnd del padre.
 *
 * @param {Object} viewField
 * @param {Object} engineApi
 * @param {Object} engineGridApi
 * @returns
 */
function setCommonAttributes(viewField, engineApi, engineGridApi) {
  const parentMetadata = viewField.parent?.metadata || {};

  const cloned = engineApi.cloneViewField(viewField, { parent: false, properties: false });
  cloned.xPath = viewField.model;
  cloned.model = getModelFromParent(viewField); // @odeniel quitamos el "CAB.LIN." delante para el ec-grid-edit Ej. 'CAB.LIN.IMP[0].TIPO' para 'LIN' => "IMP[0].TIPO"
  cloned.default = getDefaultExpression(viewField, engineApi);
  cloned.width = 1 <= parseInt(viewField.cols) && parseInt(viewField.cols) <= 8 ? parseInt(viewField.cols) * 100 : 200;
  // recuperamos el disabled del viewField (usando la función porque el disabled se herede de los padres...)
  const viewFieldDisabled = engineApi.property.getDisabled(viewField, engineApi.options.isReadOnly);

  /**
   *
   * @odeniel
   * intento de gestiona el readonly en el formBuilder,
   * de momento, solo probamos en el grid para tipo `object`.
   *
   * > Recuerdo que de momento el readonly funciona porque se pasa a disabled,
   * > El disabled se herede entonces se probaga a todos los hijos etc...
   *
   *
   */
  if (engineApi.options.isReadOnly) {
    if (viewField.type === 'object') {
      cloned.readonly = true;
    }
  }

  if (typeof viewField.pattern !== 'undefined') {
    cloned.pattern = engineApi.property.getPattern(viewField.pattern);
  }

  if (typeof viewField.required !== 'undefined') {
    cloned.required = transformExpressionToFunction(viewField.required, engineGridApi);
  }

  if (typeof viewFieldDisabled !== 'undefined') {
    cloned.disabled = transformExpressionToFunction(viewFieldDisabled, engineGridApi);
  }

  if (typeof viewField.invalid !== 'undefined') {
    cloned.invalid = transformExpressionToFunction(viewField.invalid, engineGridApi);
  }

  // columnas pinned. Si la columna (`name`) esta incluido en el array viewField.metadata.pin del padre (el LIN, tipo `array`)
  if (parentMetadata.pin && parentMetadata.pin.includes(viewField.name)) {
    cloned.pin = 'start';
  }
  // columnas pinned. Si la columna (`name`) esta incluido en el array viewField.metadata.pinEnd del padre (el LIN, tipo `array`)
  if (parentMetadata.pinEnd && parentMetadata.pinEnd.includes(viewField.name)) {
    cloned.pin = 'end';
  }

  // Para los "select", en los view, puede venir viewField.metadata.select {object<value, text>}
  // pero el grid solo acepta viewField.metadata.options {Object[]}
  // entonces transformamos:
  if (cloned?.metadata?.select && !cloned?.metadata?.options) {
    cloned.metadata.options = Object.entries(cloned.metadata.select).map(([value, text]) => {
      return { value, text };
    });
  }

  // all
  if (viewField.metadata?.mask && viewField.type === 'number') {
    if (isLocaleFormat(viewField.metadata.mask)) {
      cloned.localeFormat = viewField.metadata.mask;
    }
  }

  return cloned;
}

/**
 * Devuelve el valor por defecto en formato de expresión (es decir si es un string, encapsulado entre comillas)
 * @example si "default": "" -> '""'
 *
 * @param {object} viewField el viewField
 * @param {object} engineApi el api
 * @return {string|undefined}
 */
function getDefaultExpression(viewField, engineApi) {
  if (typeof viewField?.metadata?.defaultEval !== 'undefined') {
    return viewField.metadata.defaultEval;
  }

  if (typeof viewField.default !== 'undefined') {
    return `'${engineApi.property.getDefaultValue(viewField)}'`;
  }

  if (engineApi.utils.isRegister(viewField)) {
    return engineApi.utils.isArray(viewField) ? '[]' : '{}';
  }
}

/**
 * Devuelve el `model` formateado para el grid. (El grid no entiende de los padres)
 *
 * Es decir,
 * si el model del grid es "CAB.LIN",
 * quitamos el "CAB.LIN." delante del modelo del campo.
 *
 * @example 'CAB.LIN.EAN' => "EAN"
 * @example 'CAB.LIN.IMP[0].TIPO' => "IMP[0].TIPO"
 *
 * @param {Object} viewField
 * @returns
 */
function getModelFromParent(viewField) {
  if (!viewField.model) {
    return null;
  }
  const parentModel = findParentModel(viewField);
  return viewField.model.split(`${parentModel}.`)?.[1];

  /**
   * Recorre los viewField padres para encontrar el modelo del padre mas cercano
   * @param {object} viewField el viewField
   * @return {string}
   */
  function findParentModel(viewField) {
    const parentModel = viewField.parent.model || viewField.parent.parent.model || viewField.parent.parent.parent.model || viewField.parent.parent.parent.parent.model;

    if (!parentModel) {
      throw new Error(`Modelo del parent no encontrado para "${viewField.name}"`);
    }
    return parentModel;
  }
}

/**
 * Devuelve una expresión transformada en función para el grid.
 * Nota que puede devolver `undefined` o `boolean` directamente para optimización.
 *
 * @param {boolean|string} exp la expresion a transformar, required, disabled o invalid
 * @param {Object} engineGridApi
 * @returns {boolean|undefined|function}
 */
function transformExpressionToFunction(exp, engineGridApi) {
  if (!exp) {
    return;
  }

  if (exp === true || exp === 'true') {
    return true;
  }
  // expresión que tenemos que evaluar
  return line => {
    const res = engineGridApi.parse(exp, line);
    return res;
  };
}
